import React, { useState, useEffect } from 'react';
import { youthAPI } from '../services/api';
import { Input, PhoneInput, Select, Button, Toast, MaskedInput } from './UI';
import Icon from './Icon';

// ===== Per-card color themes =====
const CARDS = [
  { key: 'identity', title: 'Asosiy identifikatsiya', icon: 'fingerprint', syncLabel: 'JSHSHIR', color: '#6366f1', bg: '#eef2ff', required: true },
  { key: 'edu', title: "Ta'lim holati", icon: 'graduation', syncLabel: 'HEMIS', color: '#0ea5e9', bg: '#e0f2fe' },
  { key: 'emp', title: 'Ish bilan bandligi', icon: 'briefcase', syncLabel: 'Bandlik markazi', color: '#22c55e', bg: '#dcfce7' },
  { key: 'soc', title: 'Ijtimoiy toifasi', icon: 'heart', syncLabel: 'Ijtimoiy himoya', color: '#f43f5e', bg: '#ffe4e6' },
  { key: 'mig', title: 'Migratsiya holati', icon: 'plane', syncLabel: 'Migratsiya', color: '#8b5cf6', bg: '#f3e8ff' },
  { key: 'biz', title: 'Tadbirkorlik holati', icon: 'store', syncLabel: 'Soliq', color: '#f59e0b', bg: '#fef3c7' },
  { key: 'sport', title: "Sport va to'garaklar", icon: 'trophy', syncLabel: 'Sport', color: '#14b8a6', bg: '#ccfbf1' },
  { key: 'crime', title: 'Jazoni ijro etish muassasalari', icon: 'gavel', syncLabel: 'IIB', color: '#64748b', bg: '#f1f5f9' },
  { key: 'offense', title: 'Huquqbuzarlik holati', icon: 'alert', syncLabel: 'IIB', color: '#ef4444', bg: '#fef2f2' },
  { key: 'prev', title: 'Profilaktik nazorat', icon: 'radar', syncLabel: 'Profilaktika', color: '#6366f1', bg: '#eef2ff' },
];

// ===== Card wrapper =====
function Card({ title, icon, syncLabel, color, bg, required, children, onSave, saving, saved, index }) {
  const [open, setOpen] = useState(required ? true : false);
  return (
    <div style={{
      borderRadius: 16, background: '#fff', marginBottom: 14, overflow: 'hidden',
      border: open ? `1.5px solid ${color}30` : '1.5px solid #e2e8f0',
      boxShadow: open ? `0 4px 24px ${color}10` : '0 1px 3px rgba(0,0,0,.04)',
      transition: 'all .25s ease',
    }}>
      {/* Header */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', userSelect: 'none',
          background: open ? `linear-gradient(135deg, ${bg}, #fff)` : 'transparent',
          transition: 'all .25s ease',
        }}
      >
        {/* Number + Icon */}
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: open ? color : bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#fff' : color,
          transition: 'all .25s ease',
          boxShadow: open ? `0 4px 12px ${color}40` : 'none',
          position: 'relative', flexShrink: 0,
        }}>
          <Icon name={icon} size={18} />
          <span style={{
            position: 'absolute', top: -4, left: -4,
            width: 18, height: 18, borderRadius: '50%',
            background: open ? '#fff' : color,
            color: open ? color : '#fff',
            fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,.15)',
          }}>{index + 1}</span>
        </div>

        {/* Title */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
            {title}
            {required && <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#ef4444', padding: '1px 6px', borderRadius: 4 }}>Majburiy</span>}
          </div>
        </div>

        {/* Saved indicator */}
        {saved && (
          <div style={{
            padding: '4px 10px', borderRadius: 8,
            background: '#dcfce7', color: '#16a34a',
            fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
            animation: 'fadeIn .3s ease',
          }}>
            <Icon name="check" size={12} /> Saqlandi
          </div>
        )}

        {/* Sync button */}
        {syncLabel && (
          <button
            onClick={e => { e.stopPropagation(); }}
            title={`${syncLabel} bilan sinxronizatsiya (tez kunda)`}
            style={{
              padding: '6px 14px', borderRadius: 10,
              border: `1.5px solid ${color}40`,
              background: `${color}08`,
              fontSize: 11, fontWeight: 700, color: color,
              display: 'flex', alignItems: 'center', gap: 6,
              cursor: 'pointer', transition: 'all .2s', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.borderColor = color; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${color}08`; e.currentTarget.style.borderColor = `${color}40`; }}
          >
            <Icon name="sync" size={12} />
            Sinxronizatsiya
          </button>
        )}

        {/* Chevron */}
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: open ? `${color}14` : '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all .25s ease', flexShrink: 0,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={open ? color : '#94a3b8'} strokeWidth="2.5" strokeLinecap="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .25s ease' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {/* Body */}
      {open && (
        <div style={{ padding: '4px 20px 20px', borderTop: `1px solid ${color}12` }}>
          {children}
          {onSave && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={onSave}
                disabled={saving}
                style={{
                  padding: '8px 24px', borderRadius: 10, border: 'none',
                  background: saving ? '#94a3b8' : color,
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: saving ? 'none' : `0 2px 8px ${color}40`,
                  transition: 'all .2s',
                }}
              >
                {saving ? (
                  <><span style={{ width: 14, height: 14, border: '2px solid #fff4', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} /> Saqlanmoqda...</>
                ) : (
                  <><Icon name="check" size={14} color="#fff" /> Saqlash</>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Grid helpers — responsive =====
const G2 = ({ children }) => <div className="card-grid-2">{children}</div>;
const G3 = ({ children }) => <div className="card-grid-3">{children}</div>;
const Spacer = () => <div style={{ height: 10 }} />;

// ===== Select options =====
const OPT = {
  gender: [{ value: '', label: 'Tanlang' }, { value: 'male', label: 'Erkak' }, { value: 'female', label: 'Ayol' }],
  eduLevel: [{ value: '', label: 'Tanlang' }, { value: 'maktab', label: "O'rta maktab" }, { value: 'kollej', label: 'Kollej/Texnikum' }, { value: 'otm', label: "Oliy ta'lim (Bakalavr)" }, { value: 'magistr', label: 'Magistratura' }, { value: 'phd', label: 'PhD/Doktorantura' }],
  eduType: [{ value: '', label: 'Tanlang' }, { value: 'davlat', label: 'Davlat' }, { value: 'xususiy', label: 'Xususiy' }],
  eduFunding: [{ value: '', label: 'Tanlang' }, { value: 'grant', label: 'Grant' }, { value: 'kontrakt', label: 'Kontrakt' }],
  eduForm: [{ value: '', label: 'Tanlang' }, { value: 'kunduzgi', label: 'Kunduzgi' }, { value: 'sirtqi', label: 'Sirtqi' }, { value: 'kechki', label: 'Kechki' }],
  eduGrad: [{ value: '', label: 'Tanlang' }, { value: 'bitirgan', label: 'Bitirgan' }, { value: 'bitirmagan', label: 'Bitirmagan' }, { value: 'davom', label: "O'qimoqda" }],
  empStatus: [{ value: '', label: 'Tanlang' }, { value: 'ishlamoqda', label: 'Ishlamoqda' }, { value: 'ishsiz', label: 'Ishsiz' }, { value: "o'qimoqda", label: "O'qimoqda" }, { value: 'tadbirkor', label: 'Tadbirkor' }, { value: 'boshqa', label: 'Boshqa' }],
  empType: [{ value: '', label: 'Tanlang' }, { value: 'davlat', label: 'Davlat' }, { value: 'xususiy', label: 'Xususiy' }, { value: 'norasmiy', label: 'Norasmiy' }],
  yesNo: [{ value: '', label: 'Tanlang' }, { value: 'ha', label: 'Ha' }, { value: 'yoq', label: "Yo'q" }],
  orphan: [{ value: '', label: 'Tanlang' }, { value: 'yetim', label: 'Yetim' }, { value: 'yarim_yetim', label: 'Yarim yetim' }, { value: 'yoq', label: "Yo'q" }],
  housing: [{ value: '', label: 'Tanlang' }, { value: 'oz_uyi', label: "O'z uyi" }, { value: 'ijara', label: 'Ijara' }, { value: 'yotoqxona', label: 'Yotoqxona' }],
  migStatus: [{ value: '', label: 'Tanlang' }, { value: 'ichki', label: 'Ichki migratsiya' }, { value: 'tashqi', label: 'Tashqi migratsiya' }, { value: 'yoq', label: "Migrant emas" }],
  bizStatus: [{ value: '', label: 'Tanlang' }, { value: 'yatt', label: 'YaTT' }, { value: 'mchj', label: "MChJ ta'sischi" }, { value: 'yoq', label: "Yo'q" }],
  clubType: [{ value: '', label: 'Tanlang' }, { value: 'sport', label: 'Sport' }, { value: 'it', label: 'IT' }, { value: 'sanat', label: "San'at" }, { value: 'boshqa', label: 'Boshqa' }],
  disability: [{ value: '', label: 'Tanlang' }, { value: 'yoq', label: "Yo'q" }, { value: '1', label: '1-guruh' }, { value: '2', label: '2-guruh' }, { value: '3', label: '3-guruh' }],
  risk: [{ value: '', label: 'Tanlang' }, { value: 'past', label: 'Past' }, { value: "o'rta", label: "O'rta" }, { value: 'yuqori', label: 'Yuqori' }],
  salary: [{ value: '', label: 'Tanlang' }, { value: '0-1mln', label: '1 mln gacha' }, { value: '1-3mln', label: '1-3 mln' }, { value: '3-5mln', label: '3-5 mln' }, { value: '5-10mln', label: '5-10 mln' }, { value: '10+mln', label: "10 mln dan ko'p" }],
  returnProb: [{ value: '', label: 'Tanlang' }, { value: 'yuqori', label: 'Yuqori' }, { value: "o'rta", label: "O'rta" }, { value: 'past', label: 'Past' }],
};

// ===== Warning block for locked cards =====
const NeedsIdWarning = () => (
  <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 10, fontSize: 13, color: '#92400e', marginBottom: 14, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fde68a' }}>
    <Icon name="lock" size={14} color="#f59e0b" />
    Avval "Asosiy identifikatsiya" bo'limini saqlang
  </div>
);

export default function YouthForm({ youthId, onClose, onSaved }) {
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState({});
  const [toast, setToast] = useState(null);
  const isEdit = !!youthId;

  useEffect(() => {
    if (youthId) {
      setLoading(true);
      youthAPI.get(youthId).then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [youthId]);

  const s = (k, v) => setD(p => ({ ...p, [k]: v }));

  const saveCard = async (cardKey, fields) => {
    setSaving(cardKey);
    try {
      const payload = {};
      fields.forEach(f => { if (d[f] !== undefined && d[f] !== '') payload[f] = d[f]; });
      if (isEdit) {
        const res = await youthAPI.patch(youthId, payload);
        setD(prev => ({ ...prev, ...res.data }));
      } else {
        if (cardKey === 'identity') {
          const res = await youthAPI.create(payload);
          setD(res.data);
          onSaved?.(res.data);
        } else if (d.id) {
          const res = await youthAPI.patch(d.id, payload);
          setD(prev => ({ ...prev, ...res.data }));
        } else {
          setToast({ msg: "Avval asosiy ma'lumotlarni saqlang!", type: 'error' });
          setSaving(null);
          return;
        }
      }
      setSaved(p => ({ ...p, [cardKey]: true }));
      setTimeout(() => setSaved(p => ({ ...p, [cardKey]: false })), 3000);
      setToast({ msg: 'Muvaffaqiyatli saqlandi!', type: 'success' });
    } catch (e) {
      const errMsg = e.response?.data;
      let msg = "Xatolik yuz berdi";
      if (typeof errMsg === 'object') msg = Object.entries(errMsg).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ');
      setToast({ msg, type: 'error' });
    }
    setSaving(null);
  };

  if (loading) return (
    <div style={{ padding: 80, textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <div style={{ color: '#94a3b8', fontSize: 14 }}>Yuklanmoqda...</div>
    </div>
  );

  const needsId = !isEdit && !d.id;

  return (
    <div style={{ width: '100%' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Responsive grid CSS */}
      <style>{`
        .card-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .card-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
        @media (max-width: 640px) {
          .card-grid-2 { grid-template-columns: 1fr; }
          .card-grid-3 { grid-template-columns: 1fr; }
        }
        @media (min-width: 641px) and (max-width: 900px) {
          .card-grid-3 { grid-template-columns: 1fr 1fr; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottom: '2px solid #f1f5f9', flexWrap: 'wrap' }}>
        <button onClick={onClose} style={{
          width: 40, height: 40, borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b',
          transition: 'all .2s', flexShrink: 0,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: 0 }}>
            {isEdit ? `${d.last_name || ''} ${d.first_name || ''} — tahrirlash` : "Yangi yoshni ro'yxatga olish"}
          </h2>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '2px 0 0' }}>
            {d.id ? `ID: ${d.id} • ` : ''}10 ta bo'lim • Birinchi bo'lim majburiy
          </p>
        </div>
        {/* Progress */}
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {CARDS.map((c, i) => (
            <div key={c.key} style={{
              width: 24, height: 6, borderRadius: 3,
              background: saved[c.key] || (d.id && i === 0) ? c.color : '#e2e8f0',
              transition: 'background .3s',
            }} title={c.title} />
          ))}
        </div>
      </div>

      {/* 1. ASOSIY IDENTIFIKATSIYA */}
      <Card {...CARDS[0]} index={0} onSave={() => saveCard('identity', ['last_name','first_name','middle_name','gender','birth_date','birth_place','citizenship','nationality','permanent_address','temporary_address','phone','email','jshshir','passport_series','passport_number','passport_date','passport_issued_by'])} saving={saving === 'identity'} saved={saved.identity}>
        <Spacer />
        <G3>
          <Input label="Familiya" value={d.last_name || ''} onChange={v => s('last_name', v)} required placeholder="Familiya" />
          <Input label="Ism" value={d.first_name || ''} onChange={v => s('first_name', v)} required placeholder="Ism" />
          <Input label="Otasining ismi" value={d.middle_name || ''} onChange={v => s('middle_name', v)} placeholder="Otasining ismi" />
        </G3>
        <Spacer />
        <G3>
          <Select label="Jinsi" value={d.gender || ''} onChange={v => s('gender', v)} required options={OPT.gender} />
          <Input label="Tug'ilgan sana" type="date" value={d.birth_date || ''} onChange={v => s('birth_date', v)} required />
          <Input label="Tug'ilgan joy" value={d.birth_place || ''} onChange={v => s('birth_place', v)} placeholder="Shahar/tuman" />
        </G3>
        <Spacer />
        <G3>
          <Input label="Fuqaroligi" value={d.citizenship || "O'zbekiston"} onChange={v => s('citizenship', v)} />
          <Input label="Millati" value={d.nationality || ''} onChange={v => s('nationality', v)} placeholder="Millati" />
          <MaskedInput label="JSHSHIR" mask="jshshir" value={d.jshshir || ''} onChange={v => s('jshshir', v)} placeholder="14 raqamli kod" />
        </G3>
        <Spacer />
        <G2>
          <Input label="Doimiy manzil" value={d.permanent_address || ''} onChange={v => s('permanent_address', v)} placeholder="Viloyat, tuman, MFY, uy raqami" />
          <Input label="Vaqtinchalik manzil" value={d.temporary_address || ''} onChange={v => s('temporary_address', v)} placeholder="(agar farq qilsa)" />
        </G2>
        <Spacer />
        <G2>
          <PhoneInput label="Telefon" value={d.phone || ''} onChange={v => s('phone', v)} />
          <Input label="Email" value={d.email || ''} onChange={v => s('email', v)} placeholder="email@pochta.uz" />
        </G2>
        <Spacer />
        <G3>
          <MaskedInput label="Pasport seriyasi" mask="passport_series" value={d.passport_series || ''} onChange={v => s('passport_series', v)} required />
          <MaskedInput label="Pasport raqami" mask="passport_number" value={d.passport_number || ''} onChange={v => s('passport_number', v)} required />
          <Input label="Berilgan sana" type="date" value={d.passport_date || ''} onChange={v => s('passport_date', v)} />
        </G3>
        <Spacer />
        <Input label="Pasport bergan organ" value={d.passport_issued_by || ''} onChange={v => s('passport_issued_by', v)} placeholder="Buxoro viloyat IIB" />
      </Card>

      {/* 2. TA'LIM */}
      <Card {...CARDS[1]} index={1} onSave={needsId ? null : () => saveCard('edu', ['edu_level','edu_institution','edu_type','edu_course','edu_specialty','edu_funding','edu_form','edu_academic_leave','edu_graduated','edu_diploma'])} saving={saving === 'edu'} saved={saved.edu}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Ta'lim darajasi" value={d.edu_level || ''} onChange={v => s('edu_level', v)} options={OPT.eduLevel} />
          <Input label="Muassasa nomi" value={d.edu_institution || ''} onChange={v => s('edu_institution', v)} placeholder="Masalan: BuxDU" />
          <Select label="Muassasa turi" value={d.edu_type || ''} onChange={v => s('edu_type', v)} options={OPT.eduType} />
        </G3>
        <Spacer />
        <G3>
          <Input label="Kurs / sinf" value={d.edu_course || ''} onChange={v => s('edu_course', v)} placeholder="3-kurs" />
          <Input label="Yo'nalish (mutaxassislik)" value={d.edu_specialty || ''} onChange={v => s('edu_specialty', v)} placeholder="Informatika" />
          <Select label="Kontrakt / Grant" value={d.edu_funding || ''} onChange={v => s('edu_funding', v)} options={OPT.eduFunding} />
        </G3>
        <Spacer />
        <G3>
          <Select label="O'qish shakli" value={d.edu_form || ''} onChange={v => s('edu_form', v)} options={OPT.eduForm} />
          <Select label="Holati" value={d.edu_graduated || ''} onChange={v => s('edu_graduated', v)} options={OPT.eduGrad} />
          <MaskedInput label="Diplom seriya va raqami" mask="diploma" value={d.edu_diploma || ''} onChange={v => s('edu_diploma', v)} />
        </G3>
      </Card>

      {/* 3. ISH BILAN BANDLIGI */}
      <Card {...CARDS[2]} index={2} onSave={needsId ? null : () => saveCard('emp', ['emp_status','emp_workplace','emp_stir','emp_position','emp_start_date','emp_type','emp_salary_range','emp_contract','emp_registered_date','emp_certificate'])} saving={saving === 'emp'} saved={saved.emp}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Bandlik maqomi" value={d.emp_status || ''} onChange={v => s('emp_status', v)} options={OPT.empStatus} />
          <Input label="Ish joyi nomi" value={d.emp_workplace || ''} onChange={v => s('emp_workplace', v)} placeholder="Tashkilot nomi" />
          <MaskedInput label="Ish beruvchi STIR" mask="stir" value={d.emp_stir || ''} onChange={v => s('emp_stir', v)} />
        </G3>
        <Spacer />
        <G3>
          <Input label="Lavozimi" value={d.emp_position || ''} onChange={v => s('emp_position', v)} placeholder="Mutaxassis" />
          <Input label="Ish boshlanish sanasi" type="date" value={d.emp_start_date || ''} onChange={v => s('emp_start_date', v)} />
          <Select label="Ish turi" value={d.emp_type || ''} onChange={v => s('emp_type', v)} options={OPT.empType} />
        </G3>
        <Spacer />
        <G3>
          <Select label="Maosh diapazoni" value={d.emp_salary_range || ''} onChange={v => s('emp_salary_range', v)} options={OPT.salary} />
          <Input label="Mehnat shartnomasi raqami" value={d.emp_contract || ''} onChange={v => s('emp_contract', v)} placeholder="Raqam" />
          <Input label="Ishsiz ro'yxat sana" type="date" value={d.emp_registered_date || ''} onChange={v => s('emp_registered_date', v)} />
        </G3>
      </Card>

      {/* 4. IJTIMOIY TOIFASI */}
      <Card {...CARDS[3]} index={3} onSave={needsId ? null : () => saveCard('soc', ['soc_low_income','soc_benefit_type','soc_disability','soc_orphan','soc_youth_book','soc_housing','soc_aid_history'])} saving={saving === 'soc'} saved={saved.soc}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Kam ta'minlangan" value={d.soc_low_income ? 'ha' : (d.soc_low_income === false ? 'yoq' : '')} onChange={v => s('soc_low_income', v === 'ha')} options={OPT.yesNo} />
          <Input label="Nafaqa turi" value={d.soc_benefit_type || ''} onChange={v => s('soc_benefit_type', v)} placeholder="(ixtiyoriy)" />
          <Select label="Nogironlik guruhi" value={d.soc_disability || ''} onChange={v => s('soc_disability', v)} options={OPT.disability} />
        </G3>
        <Spacer />
        <G3>
          <Select label="Yetim / yarim yetim" value={d.soc_orphan || ''} onChange={v => s('soc_orphan', v)} options={OPT.orphan} />
          <Input label="Temir daftar / Yoshlar daftari" value={d.soc_youth_book || ''} onChange={v => s('soc_youth_book', v)} placeholder="Statusi" />
          <Select label="Uy-joy sharoiti" value={d.soc_housing || ''} onChange={v => s('soc_housing', v)} options={OPT.housing} />
        </G3>
      </Card>

      {/* 5. MIGRATSIYA */}
      <Card {...CARDS[4]} index={4} onSave={needsId ? null : () => saveCard('mig', ['mig_status','mig_country','mig_departure_date','mig_return_date','mig_contract','mig_registered','mig_return_probability'])} saving={saving === 'mig'} saved={saved.mig}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Migratsiya maqomi" value={d.mig_status || ''} onChange={v => s('mig_status', v)} options={OPT.migStatus} />
          <Input label="Qaysi davlatda" value={d.mig_country || ''} onChange={v => s('mig_country', v)} placeholder="Rossiya" />
          <Input label="Chiqish sanasi" type="date" value={d.mig_departure_date || ''} onChange={v => s('mig_departure_date', v)} />
        </G3>
        <Spacer />
        <G3>
          <Input label="Qaytish sanasi" type="date" value={d.mig_return_date || ''} onChange={v => s('mig_return_date', v)} />
          <Select label="Qaytish ehtimoli" value={d.mig_return_probability || ''} onChange={v => s('mig_return_probability', v)} options={OPT.returnProb} />
          <Select label="Ro'yxatdan o'tgan" value={d.mig_registered ? 'ha' : (d.mig_registered === false ? 'yoq' : '')} onChange={v => s('mig_registered', v === 'ha')} options={OPT.yesNo} />
        </G3>
      </Card>

      {/* 6. TADBIRKORLIK */}
      <Card {...CARDS[5]} index={5} onSave={needsId ? null : () => saveCard('biz', ['biz_status','biz_stir','biz_activity_type','biz_reg_date','biz_active','biz_tax_debt','biz_employees','biz_subsidy'])} saving={saving === 'biz'} saved={saved.biz}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Tadbirkorlik maqomi" value={d.biz_status || ''} onChange={v => s('biz_status', v)} options={OPT.bizStatus} />
          <MaskedInput label="STIR" mask="stir" value={d.biz_stir || ''} onChange={v => s('biz_stir', v)} />
          <Input label="Faoliyat turi (OKED)" value={d.biz_activity_type || ''} onChange={v => s('biz_activity_type', v)} placeholder="IT xizmatlari" />
        </G3>
        <Spacer />
        <G3>
          <Input label="Ro'yxatdan o'tgan sana" type="date" value={d.biz_reg_date || ''} onChange={v => s('biz_reg_date', v)} />
          <Input label="Xodimlar soni" type="number" value={d.biz_employees ?? ''} onChange={v => s('biz_employees', parseInt(v) || 0)} placeholder="0" />
          <Select label="Soliq qarzi" value={d.biz_tax_debt ? 'ha' : (d.biz_tax_debt === false ? 'yoq' : '')} onChange={v => s('biz_tax_debt', v === 'ha')} options={OPT.yesNo} />
        </G3>
      </Card>

      {/* 7. SPORT */}
      <Card {...CARDS[6]} index={6} onSave={needsId ? null : () => saveCard('sport', ['sport_club_name','sport_club_type','sport_institution','sport_start_date','sport_rating','sport_competitions','sport_rank','sport_certificates'])} saving={saving === 'sport'} saved={saved.sport}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Input label="To'garak nomi" value={d.sport_club_name || ''} onChange={v => s('sport_club_name', v)} placeholder="Buxoro IT klubi" />
          <Select label="To'garak turi" value={d.sport_club_type || ''} onChange={v => s('sport_club_type', v)} options={OPT.clubType} />
          <Input label="Muassasa nomi" value={d.sport_institution || ''} onChange={v => s('sport_institution', v)} placeholder="Sport maktab" />
        </G3>
        <Spacer />
        <G3>
          <Input label="Ishtirok boshlanish sanasi" type="date" value={d.sport_start_date || ''} onChange={v => s('sport_start_date', v)} />
          <Input label="Sport razryadi" value={d.sport_rank || ''} onChange={v => s('sport_rank', v)} placeholder="1-razryad" />
          <Input label="Reyting / natija" value={d.sport_rating || ''} onChange={v => s('sport_rating', v)} placeholder="(ixtiyoriy)" />
        </G3>
      </Card>

      {/* 8. JAZO */}
      <Card {...CARDS[7]} index={7} onSave={needsId ? null : () => saveCard('crime', ['crime_convicted','crime_court_order','crime_article','crime_punishment_type','crime_duration','crime_start_date','crime_end_date','crime_parole'])} saving={saving === 'crime'} saved={saved.crime}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Sudlanganlik holati" value={d.crime_convicted ? 'ha' : (d.crime_convicted === false ? 'yoq' : '')} onChange={v => s('crime_convicted', v === 'ha')} options={OPT.yesNo} />
          <Input label="Sud qarori raqami" value={d.crime_court_order || ''} onChange={v => s('crime_court_order', v)} placeholder="Raqam" />
          <Input label="Jinoyat moddasi" value={d.crime_article || ''} onChange={v => s('crime_article', v)} placeholder="168-modda" />
        </G3>
        <Spacer />
        <G3>
          <Input label="Jazo turi" value={d.crime_punishment_type || ''} onChange={v => s('crime_punishment_type', v)} placeholder="Shartli jazo" />
          <Input label="Jazo muddati" value={d.crime_duration || ''} onChange={v => s('crime_duration', v)} placeholder="2 yil" />
          <Select label="Shartli ozod qilingan" value={d.crime_parole ? 'ha' : (d.crime_parole === false ? 'yoq' : '')} onChange={v => s('crime_parole', v === 'ha')} options={OPT.yesNo} />
        </G3>
        <Spacer />
        <G2>
          <Input label="Jazo boshlanish sanasi" type="date" value={d.crime_start_date || ''} onChange={v => s('crime_start_date', v)} />
          <Input label="Jazo tugash sanasi" type="date" value={d.crime_end_date || ''} onChange={v => s('crime_end_date', v)} />
        </G2>
      </Card>

      {/* 9. HUQUQBUZARLIK */}
      <Card {...CARDS[8]} index={8} onSave={needsId ? null : () => saveCard('offense', ['offense_case_number','offense_type','offense_date','offense_authority','offense_repeated'])} saving={saving === 'offense'} saved={saved.offense}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Input label="Ma'muriy ish raqami" value={d.offense_case_number || ''} onChange={v => s('offense_case_number', v)} placeholder="Raqam" />
          <Input label="Qonunbuzarlik turi" value={d.offense_type || ''} onChange={v => s('offense_type', v)} placeholder="Turi" />
          <Input label="Sodir etilgan sana" type="date" value={d.offense_date || ''} onChange={v => s('offense_date', v)} />
        </G3>
        <Spacer />
        <G2>
          <Input label="Qaror chiqargan organ" value={d.offense_authority || ''} onChange={v => s('offense_authority', v)} placeholder="Organ nomi" />
          <Select label="Takroriy holat" value={d.offense_repeated ? 'ha' : (d.offense_repeated === false ? 'yoq' : '')} onChange={v => s('offense_repeated', v === 'ha')} options={OPT.yesNo} />
        </G2>
      </Card>

      {/* 10. PROFILAKTIK */}
      <Card {...CARDS[9]} index={9} onSave={needsId ? null : () => saveCard('prev', ['prev_registered','prev_type','prev_inspector','prev_start_date','prev_end_date','prev_plan','prev_risk_level'])} saving={saving === 'prev'} saved={saved.prev}>
        {needsId && <NeedsIdWarning />}
        <Spacer />
        <G3>
          <Select label="Profilaktik hisobda" value={d.prev_registered ? 'ha' : (d.prev_registered === false ? 'yoq' : '')} onChange={v => s('prev_registered', v === 'ha')} options={OPT.yesNo} />
          <Input label="Nazorat turi" value={d.prev_type || ''} onChange={v => s('prev_type', v)} placeholder="Turi" />
          <Input label="Biriktirilgan inspektor" value={d.prev_inspector || ''} onChange={v => s('prev_inspector', v)} placeholder="F.I.O" />
        </G3>
        <Spacer />
        <G3>
          <Input label="Nazorat boshlanish" type="date" value={d.prev_start_date || ''} onChange={v => s('prev_start_date', v)} />
          <Input label="Nazorat tugash" type="date" value={d.prev_end_date || ''} onChange={v => s('prev_end_date', v)} />
          <Select label="Xavf darajasi" value={d.prev_risk_level || ''} onChange={v => s('prev_risk_level', v)} options={OPT.risk} />
        </G3>
      </Card>

      <div style={{ height: 40 }} />
    </div>
  );
}
